import React, { useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Cpu, Layers, Database, Lock, Play, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import { useTranslation } from 'react-i18next';

const Technology = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-black text-white min-h-screen">
      <PageTitle title="Technology - How RepresenX Works" />
      {/* BLOCK 1 - HERO */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 scanline">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#06b6d4" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 reveal-on-scroll">
              <h1 className="text-5xl lg:text-7xl font-bold animate-fadeUp">{t('technology.title')}</h1>
              <p className="text-2xl text-cyan-400 font-light shimmer mb-4">
                {t('technology.subtitle')}
              </p>
              <p className="text-lg text-gray-300 leading-relaxed max-w-xl">
                {t('technology.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white">
                  {t('technology.viewArchitecture')}
                </Button>
                <Button variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
                  {t('technology.learnGovernance')}
                </Button>
              </div>
            </div>
            
            <div className="flex items-center justify-center reveal-on-scroll">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-cyan-700/10 rounded-full blur-3xl animate-glow-pulse" />
                <svg viewBox="0 0 200 200" className="w-full h-full relative z-10">
                  <circle cx="100" cy="100" r="70" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0.3" />
                  <circle cx="100" cy="100" r="50" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0.5" />
                  <circle cx="100" cy="100" r="30" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0.7" />
                  <circle cx="100" cy="100" r="10" fill="#06b6d4" opacity="0.9" />
                  
                  <line x1="100" y1="30" x2="100" y2="170" stroke="#06b6d4" strokeWidth="0.5" opacity="0.5" />
                  <line x1="30" y1="100" x2="170" y2="100" stroke="#06b6d4" strokeWidth="0.5" opacity="0.5" />
                  <line x1="50" y1="50" x2="150" y2="150" stroke="#06b6d4" strokeWidth="0.5" opacity="0.5" />
                  <line x1="150" y1="50" x2="50" y2="150" stroke="#06b6d4" strokeWidth="0.5" opacity="0.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 2 - ARCHITECTURE */}
      <section className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-8 reveal-on-scroll">
            {t('technology.architectureTitle')}
          </h2>
          <p className="text-xl text-gray-300 text-center mb-16 max-w-3xl mx-auto reveal-on-scroll">
            {t('technology.architectureSubtitle')}
          </p>

          <div className="space-y-8">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 reveal-on-scroll neon-border-hover">
              <CardContent className="p-10">
                <div className="flex flex-col md:flex-row items-center justify-between space-y-8 md:space-y-0 md:space-x-8">
                  <div className="flex-1 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-4 energy-pulse">
                      <Database className="text-cyan-400" size={36} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{t('technology.inputLayer')}</h3>
                    <p className="text-gray-400 text-sm">{t('technology.inputLayerDesc')}</p>
                  </div>

                  <div className="text-cyan-400 text-4xl">→</div>

                  <div className="flex-1 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-4">
                      <Cpu className="text-cyan-400" size={36} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{t('technology.processingLayers')}</h3>
                    <p className="text-gray-400 text-sm">{t('technology.processingLayersDesc')}</p>
                  </div>

                  <div className="text-cyan-400 text-4xl">→</div>

                  <div className="flex-1 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-4">
                      <Layers className="text-cyan-400" size={36} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{t('technology.outputLayer')}</h3>
                    <p className="text-gray-400 text-sm">{t('technology.outputLayerDesc')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* BLOCK 3 - SIMULATION LAYERS */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-16 reveal-on-scroll">
            {t('technology.simulationTitle')}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Cpu,
                title: t('technology.styleLayer'),
                description: t('technology.styleLayerDesc')
              },
              {
                icon: Layers,
                title: t('technology.expressionLayer'),
                description: t('technology.expressionLayerDesc')
              },
              {
                icon: Database,
                title: t('technology.memoryNodes'),
                description: t('technology.memoryNodesDesc')
              },
              {
                icon: Lock,
                title: t('technology.ethicalGuardrails'),
                description: t('technology.ethicalGuardrailsDesc')
              }
            ].map((layer, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-b from-gray-900 to-gray-800 border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 group reveal-on-scroll lift-on-hover"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-8 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-2 group-hover:scale-110 transition-transform duration-300 energy-pulse">
                    <layer.icon className="text-cyan-400" size={28} />
                  </div>
                  <div className="h-1 w-12 bg-gradient-to-r from-cyan-500 to-transparent mx-auto liquid-flow" />
                  <h3 className="text-xl font-semibold">{layer.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{layer.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 3.5 - IDENTITY & MEMORY ARCHITECTURE */}
      <section className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-8 reveal-on-scroll">
            {t('technology.identityMemoryTitle')}
          </h2>
          <p className="text-xl text-gray-300 text-center mb-16 max-w-4xl mx-auto reveal-on-scroll">
            {t('technology.identityMemoryDesc')}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Database,
                title: t('technology.episodicMemory'),
                description: t('technology.episodicMemoryDesc')
              },
              {
                icon: Layers,
                title: t('technology.semanticKnowledge'),
                description: t('technology.semanticKnowledgeDesc')
              },
              {
                icon: Cpu,
                title: t('technology.styleValuesLayer'),
                description: t('technology.styleValuesLayerDesc')
              }
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-b from-gray-900 to-gray-800 border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 reveal-on-scroll lift-on-hover"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-8 space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-2 energy-pulse">
                    <item.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCK 3.6 - PERSONA CARDS */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-8 reveal-on-scroll">
            {t('technology.personaCardsTitle')}
          </h2>
          <p className="text-xl text-gray-300 text-center mb-12 max-w-4xl mx-auto reveal-on-scroll">
            {t('technology.personaCardsDesc')}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: t('technology.toneStyle'), detail: t('technology.toneStyleDetail') },
              { label: t('technology.linguisticPatterns'), detail: t('technology.linguisticPatternsDetail') },
              { label: t('technology.valuesPrinciples'), detail: t('technology.valuesPrinciplesDetail') },
              { label: t('technology.safetyRules'), detail: t('technology.safetyRulesDetail') }
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 reveal-on-scroll"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-6 text-center space-y-3">
                  <div className="h-1 w-12 bg-gradient-to-r from-cyan-500 to-transparent mx-auto liquid-flow" />
                  <h4 className="font-semibold text-lg">{item.label}</h4>
                  <p className="text-gray-400 text-sm">{item.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-gray-400 text-sm mt-8 reveal-on-scroll">
            {t('technology.personaCardsFooter')}
          </p>
        </div>
      </section>

      {/* BLOCK 3.7 - GOVERNANCE LAYER */}
      <section className="py-24 lg:py-32 bg-[#0B1F2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-8 reveal-on-scroll">
            {t('technology.governanceTitle')}
          </h2>
          <p className="text-xl text-gray-300 text-center mb-16 max-w-4xl mx-auto reveal-on-scroll">
            {t('technology.governanceDesc')}
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-red-500/30 reveal-on-scroll">
              <CardContent className="p-10 space-y-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Lock className="text-red-400" size={32} />
                  <h3 className="text-2xl font-bold text-red-400">{t('technology.blockedByDesign')}</h3>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">×</span>
                    {t('technology.identityVerification')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">×</span>
                    {t('technology.financialTransfers')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">×</span>
                    {t('technology.medicalLegalAdvice')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">×</span>
                    {t('technology.politicalPersuasion')}
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 reveal-on-scroll" style={{ transitionDelay: '100ms' }}>
              <CardContent className="p-10 space-y-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="text-cyan-400" size={32} />
                  <h3 className="text-2xl font-bold text-cyan-400">{t('technology.safetyControls')}</h3>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">✓</span>
                    {t('technology.policyAwareFilters')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">✓</span>
                    {t('technology.refusalTemplates')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">✓</span>
                    {t('technology.continuousLogging')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2">✓</span>
                    {t('technology.watermarkedAudio')}
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* BLOCK 4 - SECURITY */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold">{t('technology.securityTitle')}</h2>
              <ul className="space-y-4">
                {[
                  t('technology.industryEncryption'),
                  t('technology.transparentControls'),
                  t('technology.localStorage'),
                  t('technology.valueBehavior')
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full" />
                    </div>
                    <span className="text-lg text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-center">
              <div className="grid grid-cols-2 gap-6">
                {[Lock, Lock, Lock, Lock].map((Icon, idx) => (
                  <div key={idx} className="w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-cyan-700/5 rounded-2xl flex items-center justify-center border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300">
                    <Icon className="text-cyan-400" size={48} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 5 - DEMO */}
      <section className="py-24 lg:py-32 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            {t('technology.demoTitle')}
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            {t('technology.demoDesc')}
          </p>
          
          <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-cyan-500/30 flex items-center justify-center group hover:border-cyan-500/60 transition-all duration-300">
            <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white text-lg px-8 py-6">
              <Play className="mr-2" size={24} />
              {t('technology.watchDemo')}
            </Button>
            <div className="absolute inset-0 bg-cyan-500/5 blur-xl rounded-2xl group-hover:bg-cyan-500/10 transition-all duration-300" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Technology;
